import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Card,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  InputBase,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/system';
import { Formik } from 'formik';
import { retrievePasswordSchema } from '../../../../schemas/auth.request';
import DoneIcon from '@mui/icons-material/Done';
import { useSendResetEmailMutation } from '../../../../store/api/userApi';
import { toast } from 'react-toastify';
import SentEmailPage from '../../../SentEmailPage';

const RetrievePasswordContainer = styled(Card)(({ theme }) => ({
  display: 'flex',
  width: '393px',
  height: '310px',
  margin: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(2px)',
  borderRadius: '0',
  [theme.breakpoints.down('600')]: {
    width: '363px',
  },
}));

const RetrievePasswordContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '373px',
  height: '290px',
  padding: '40px',
  margin: 'auto',
  border: '1px solid #AA9178',
  gap: '35px',
  [theme.breakpoints.down('600')]: {
    width: '343px',
  },
}));

const RetrievePasswordTitleContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
});
const RetrievePasswordTitle = styled(Typography)({
  fontFamily: 'Fjalla One',
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '25px',
  alignItems: 'center',
  color: '#FFFFFF',
});

const AuthenticationInputLabel = styled(InputLabel)(() => ({
  color: '#FFFFFF',
  fontFamily: 'Raleway',
  fontSize: '16px',
  lineHeight: '16px',
}));

const AuthenticationBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  margin: '20px 0 40px',
  height: '40px',
}));

const AuthenticationInput = styled(InputBase)(() => ({
  background: '#FFFFFF',
  border: '2px solid #AA9178',
  borderRadius: '5px',
}));

const ResetButton = styled(Button)(() => ({
  width: '200px',
  height: '26px',
  textTransform: 'none',
  fontFamily: 'Fjalla One',
  fontSize: '14px',
  lineHeight: '18px',
  color: '#FFFFFF',
  backgroundColor: '#AA9178',
  borderRadius: '0',
}));
const Promptdetails = styled(Typography)({
  fontFamily: 'Raleway',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '18px',
  color: '#FFFFFF',
});

const PromptButton = styled(Button)({
  width: '92px',
  height: '27px',
  backgroundColor: '#AA9178',
  borderRadius: 0,
});

const DirectHome = styled('a')({
  display: 'flex',
  fontFamily: 'Fjalla One',
  fontWeight: 400,
  fontSize: '14px',
  color: '#FFFFFF',
  textDecoration: 'none',
  alignItems: 'center',
});

const ErrorFormHelperText = styled(FormHelperText)(() => ({
  fontFamily: 'Raleway',
}));

const validateSchema = (values) => {
  const errors = {};
  const valid = retrievePasswordSchema.validate(values, { abortEarly: false });
  if (valid.error) {
    valid.error.details.forEach((err) => {
      errors[err.path.join('')] = err.message;
    });
  }
  return errors;
};

const RetrievePasswordCard = (props) => {
  const [sendResetEmail, { isLoading, isSuccess, isError, error }] = useSendResetEmailMutation();

  useEffect(() => {
    if (isError) {
      toast.error(error.data.error, { toastId: 'error' });
    }
  }, [isError]);

  return (
    <RetrievePasswordContainer>
      <RetrievePasswordContent>
        {isSuccess ? (
          <SentEmail />
        ) : (
          <>
            <RetrievePasswordTitleContainer>
              <RetrievePasswordTitle>Find your CoffeeTopia account</RetrievePasswordTitle>
            </RetrievePasswordTitleContainer>
            <Formik
              initialValues={{
                userInput: '',
              }}
              validate={validateSchema}
              onSubmit={async (values) => {
                await sendResetEmail({ email: values.userInput });
              }}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
                <form onSubmit={handleSubmit}>
                  <FormControl variant="standard" sx={{ width: '100%' }}>
                    <AuthenticationInputLabel
                      shrink
                      sx={{ '&.Mui-focused': { color: '#FFFFFF' } }}
                      htmlFor="userInput"
                    >
                      What's your email?
                    </AuthenticationInputLabel>
                    <AuthenticationBox>
                      <AuthenticationInput
                        id="userInput"
                        type="userInput"
                        value={values.userInput}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.userInput && errors.userInput && (
                        <ErrorFormHelperText error>{errors.userInput}</ErrorFormHelperText>
                      )}
                    </AuthenticationBox>
                  </FormControl>
                  <ResetButton type="submit" onClick={props.toggleBool}>
                    Send a Password Reset Email
                  </ResetButton>
                </form>
              )}
            </Formik>
          </>
        )}
      </RetrievePasswordContent>
    </RetrievePasswordContainer>
  );
};
function SentEmail(props) {
  return (
    <>
      <RetrievePasswordTitleContainer>
        <DoneIcon sx={{ color: 'white' }} />
        <RetrievePasswordTitle>Reset email has been sent</RetrievePasswordTitle>
      </RetrievePasswordTitleContainer>
      <Promptdetails>
        A password reset email has been sent to the email address on file for your account, but may
        take several minutes to show up in your inbox. Please wait at least 10 minutes before
        attempting another reset.
      </Promptdetails>
      <PromptButton>
        <DirectHome href="/signin">Retry Login</DirectHome>
      </PromptButton>
    </>
  );
}

export default RetrievePasswordCard;
